import { stringify } from 'querystring';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useApiClient } from '@travel/api-client';
import useCurrencyChangeCallback from '@travel/traveler-core/hooks/useCurrencyChangeCallback';
import { useDatesChange } from '@travel/traveler-core/hooks/useDatesChange';
import useDeviceType, {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
  TABLET_SCREEN,
} from '@travel/traveler-core/hooks/useDeviceType';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { seoKeys, toSEOSearchString } from '@travel/traveler-core/utils/seo';
import { FlatButton } from '@travel/ui';
import FilterTag from '@travel/ui/components/TagFilter/filterTag';
import { cx, isNotEmptyArray } from '@travel/utils';

import AmountInput from 'components/AmountInput';
import DateInput from 'components/DateInput';
import Form from 'components/Form';
import ProviderFilterTags from 'components/ProviderFilterTags';
import SalesPromotionInput from 'components/SalesPromotionInput';
import SortSelectBox from 'components/SortSelectBox';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaultValue';
import { TAB_ID } from 'constants/providerInfoTabIds';
import useFilterCount, { PriceRangeType } from 'hooks/useFilterCount';
import useProviderFeatureFilter from 'hooks/useProviderFeatureFilter';
import { getSelectedNavSection } from 'store/providerInformation/selectors';
import { fetchProviderPlanList } from 'store/providerPlanList/actions';
import { fetchPlanList } from 'store/providerPlanList/apis';
import { getFilters as getFiltersPlanList, getPlansTotal } from 'store/providerPlanList/selectors';
import { fetchProviderRatePlan } from 'store/providerRatePlan/actions';
import { fetchAll } from 'store/providerRatePlan/apis';
import {
  getFilters as getFiltersRoomList,
  getItems,
  getPlansTotal as getRoomsTotal,
  getSelectedObjectivesFeatureIdMapping,
} from 'store/providerRatePlan/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';
import HorizontalNavScroll from '../../../../components/HorizontalNavScroll';
import { PlanFilter } from '../RoomList';
import FilterButton from './FilterButton';

import { SortKeysType } from 'constants/provider';
import { Translate } from 'core/translate';
import { ProviderInfoListTypes, ProviderInfoQuery } from 'ProviderInformation-Types';
import { RatePlanFilterFeature } from 'ProviderRatePlan-Types';
import { isClient } from 'store/utils';
import { getAllowedRedirectSearchQuery } from 'utils/search';
import { getRatePlanRequestBody } from '../../resolver';

import { getLimitationsForCurrentMarket } from 'store/markets/selectors';

import styles from './searchForm.module.scss';

type Props = {
  className?: string;
  /** String to define the current provider id */
  pageIdentifier: string;
  /** Where the search bar is on */
  listType: ProviderInfoListTypes;
  query: ProviderInfoQuery;
  /** Flag to define whether the data is being fetched or not */
  isFetching?: boolean;
  /** Callback to be called when we need to re-fetch plan items */
  updateURL?: (search: string) => void;
  /** Whether date picker auto open after selected place */
  isAutoOpenDatePicker?: boolean;
  /** Set auto open date picker */
  setIsAutoOpenDatePicker?: (autoOpen: boolean) => void;
  /** is Open/Closed should be used together width toggle prop func */
  isCouponAccordionOpen?: boolean;
  /** toggle coupon accordion prop func */
  onToggleCouponAccordion?: () => void;
  /** Element from parent component that toggles tabs */
  listingTypeSelectorTabList?: JSX.Element;
  tagFilterAndTabListRef?: React.Ref<HTMLDivElement>;
  tabListWidth?: number;
};

const QUICK_FILTER_TAG_TESTID = 'quick-filter-tags';
const MAXIMUM_QUICK_FILTERS = 4;
const PROVIDER_INFO = 'providerInfo';

function SearchForm(props: Props) {
  const {
    query,
    listType,
    isAutoOpenDatePicker,
    setIsAutoOpenDatePicker,
    listingTypeSelectorTabList,
    updateURL,
  } = props;
  const apiClient = useApiClient();
  const formEl = useRef<HTMLFormElement>(null);
  const objectiveFeatureMappingIds = useSelector(getSelectedObjectivesFeatureIdMapping);

  const items = useSelector(getItems);

  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isPC = deviceType === DESKTOP_SCREEN;
  const isTab = deviceType === TABLET_SCREEN;
  const isRoomListType = listType === PROVIDER_LIST_TYPE.ROOM_LIST;
  const params = { pageIdentifier: props.pageIdentifier };

  const searchFreeCancellation = query.freeCancellation === 'true' ? true : false;
  const searchedMaxPrice = query.maxPrice ? Number(query.maxPrice) : null;
  const searchedMinPrice = query.minPrice ? Number(query.minPrice) : null;

  const currentCoupon = useSelector(getCouponDetails(query.salesPromotionId || ''));
  const [maxPrice, setMaxPrice] = useState<PriceRangeType>(searchedMaxPrice);
  const [minPrice, setMinPrice] = useState<PriceRangeType>(searchedMinPrice);
  const [sortKey, setSortKey] = useState(props.query?.sortKey || 'price_low_to_high');
  const [freeCancellation, setFreeCancellation] = useState(searchFreeCancellation);
  const { features, setFeatures, searchFeatures } = useProviderFeatureFilter(
    objectiveFeatureMappingIds,
    query.features,
  );
  const quickFilterWrapperRef = useRef<HTMLDivElement>(null);

  const filterOptionsRoomList = useSelector(getFiltersRoomList);
  const filterOptionsPlanList = useSelector(getFiltersPlanList);
  const initialOptions = isRoomListType ? filterOptionsRoomList : filterOptionsPlanList;
  const selectedNavSection = useSelector(getSelectedNavSection);

  const [filterOptions, setFilterOptions] = useState(initialOptions);
  const roomListTotal = useSelector(getRoomsTotal);
  const planListTotal = useSelector(getPlansTotal);
  const [total, setTotal] = useState(isRoomListType ? roomListTotal : planListTotal);
  const [quickFilters, setQuickFilters] = useState<RatePlanFilterFeature[]>([]);
  const [filterButtonWidth, setFilterButtonWidth] = useState<number>(0);
  const [quickFiltersThresholdWidth, setQuickFiltersThresholdWidth] = useState<number>(0);
  const [isScrollingDown, setIsScrollingDown] = useState<boolean>(false);
  const [isPricePopupOpen, setPricePopupOpen] = useState(false);
  const [hidePricePopup, setHidePricePopup] = useState(false);
  const prevScrollPos = useRef(0);
  const filterCount = useFilterCount(
    { features, maxPrice, minPrice, freeCancellation },
    filterOptions,
  );
  const limitations = useSelector(getLimitationsForCurrentMarket);

  const onPage = query?.startDate || query?.endDate ? 'providerInfoDated' : 'providerInfoUndated';
  const { isDatesSet, onDatesSet } = useDatesChange(
    Boolean(props.query?.startDate) && Boolean(props.query?.endDate),
  );
  useEffect(() => {
    setTotal(isRoomListType ? roomListTotal : planListTotal);
    setFilterOptions(initialOptions);
    if (isNotEmptyArray(initialOptions)) {
      const getFirstFeature = initialOptions
        .map(item => item.features?.[0] ?? null)
        .filter(feature => feature && feature?.name) as RatePlanFilterFeature[];
      const uniqueFeatureFilters = Array.from(
        new Set(getFirstFeature.map(feature => JSON.stringify(feature))),
      ).map(item => JSON.parse(item)) as RatePlanFilterFeature[];
      const quickFilters = uniqueFeatureFilters.slice(
        0,
        Math.min(uniqueFeatureFilters.length, MAXIMUM_QUICK_FILTERS),
      );
      setQuickFilters(quickFilters);
    } else {
      setQuickFilters([]);
    }
  }, [initialOptions, isRoomListType, planListTotal, roomListTotal]);

  // This function required to toggles the visibility of applied filters based on the scroll direction.
  const updateScrollDirection = (currentScrollPos: number): void => {
    setIsScrollingDown(currentScrollPos > prevScrollPos.current);
    prevScrollPos.current = currentScrollPos;
  };

  const handleScroll = useCallback(() => {
    if (!isClient) {
      return;
    }
    const currentScrollPos = window.scrollY;
    if (selectedNavSection?.id === TAB_ID.ROOMS_AND_PLANS && isPC) {
      updateScrollDirection(currentScrollPos);
    }
  }, [isPC, selectedNavSection?.id]);

  useEffect(() => {
    if (!isClient) {
      return;
    }
    prevScrollPos.current = window.scrollY;
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, isScrollingDown]);

  // Calculate the width of the quick filter wrapper to display it between the filter button and tab list
  useEffect(() => {
    if (props.tabListWidth && quickFilterWrapperRef.current && isClient) {
      const computedStyle = window.getComputedStyle(quickFilterWrapperRef.current);
      const marginLeft = parseInt(computedStyle.marginLeft || '0', 10);
      const marginRight = parseInt(computedStyle.marginRight || '0', 10);
      setQuickFiltersThresholdWidth(
        filterButtonWidth + props.tabListWidth - (marginLeft + marginRight),
      ); // Subtracting the margin of quickFilterWrapper
    }
  }, [filterButtonWidth, props.tabListWidth, quickFilterWrapperRef, quickFilters]);

  const toggleFilterOptions = useCallback(
    (options: PlanFilter) => {
      let nextFeatures = [...features];

      if (isNotEmptyArray(options.features)) {
        const filteredFeatures = features.filter(feature => !options.features?.includes(feature));
        const newlyAddedFeatures = options.features.filter(feature => !features?.includes(feature));

        nextFeatures = [...filteredFeatures, ...newlyAddedFeatures];
      }

      const nextFilterOptions = {
        maxPrice: options.maxPrice,
        minPrice: options.minPrice,
        freeCancellation: options.freeCancellation,
        features: nextFeatures,
      };

      setFeatures(nextFilterOptions.features);
      setMaxPrice(nextFilterOptions.maxPrice);
      setMinPrice(nextFilterOptions.minPrice);
      setFreeCancellation(Boolean(nextFilterOptions.freeCancellation));
      return nextFilterOptions;
    },
    [features, setFeatures],
  );

  const fetchPlanItems = useCallback(
    (search, options: PlanFilter) => {
      const reqBody = getRatePlanRequestBody(
        props.pageIdentifier,
        {
          ...search,
          ...options,
        },
        currentCoupon,
      );
      dispatch(fetchProviderRatePlan(reqBody));
      dispatch(fetchProviderPlanList(reqBody));
    },
    [currentCoupon, dispatch, props.pageIdentifier],
  );

  const onCurrencyChange = useCallback(
    () => fetchPlanItems(props.query, { features, maxPrice, minPrice }),
    [maxPrice, minPrice, features, fetchPlanItems, props.query],
  );
  useCurrencyChangeCallback(onCurrencyChange);

  const updateTotalNumber = async (options: PlanFilter) => {
    try {
      const reqBody = getRatePlanRequestBody(
        props.pageIdentifier,
        {
          ...props.query,
          ...options,
          freeCancellation: String(options.freeCancellation),
        },
        currentCoupon,
      );
      if (isRoomListType) {
        const response = await fetchAll(apiClient, reqBody);
        setTotal(response?.items?.length || 0);
      } else {
        const response = await fetchPlanList(apiClient, reqBody);
        setTotal(response?.plans?.length || 0);
      }
    } catch (error) {
      // fallback case
      setTotal(isRoomListType ? roomListTotal : planListTotal);
    }
  };

  const onPriceSliderUpdate = (min?: number | null, max?: number | null) => {
    setMaxPrice(max);
    setMinPrice(min);
  };

  const onFreeCancellationUpdate = () => {
    setFreeCancellation(!freeCancellation);
  };

  // execute when every single value is updated
  const onFilterChange = (toggleOptions: PlanFilter) => {
    const nextFilterOptions = toggleFilterOptions({
      features: EMPTY_ARRAY,
      maxPrice,
      minPrice,
      freeCancellation,
      ...toggleOptions,
    });

    updateTotalNumber(nextFilterOptions);
  };

  const onClearFilter = () => {
    setFeatures(EMPTY_ARRAY);
    setMaxPrice(null);
    setMinPrice(null);
    setFreeCancellation(false);
    updateTotalNumber({
      maxPrice: null,
      minPrice: null,
      freeCancellation: false,
      features: EMPTY_ARRAY,
    });
  };

  const onResetFilter = () => {
    // reset filter state to the recent searched one
    setMinPrice(searchedMinPrice);
    setMaxPrice(searchedMaxPrice);
    setFeatures(searchFeatures);
    setFreeCancellation(searchFreeCancellation);
    setTotal(isRoomListType ? roomListTotal : planListTotal);
  };

  const triggerFormSubmit = useCallback(() => {
    if (formEl.current) {
      // NOTE: To trigger the form submit (See more: https://github.com/facebook/react/issues/6796)
      formEl.current?.dispatchEvent(new Event('submit'));
    }
  }, []);

  const onRemoveCoupon = useCallback(() => {
    query['isCouponSpecified'] = undefined;
    updateURL?.(toSEOSearchString({ ...query, salesPromotionId: undefined }, limitations));
  }, [updateURL, query]);

  // queryObj is from form values (being used with condition only)
  const onSubmitCondition = ({ queryObj = EMPTY_OBJECT }) => {
    if (formEl.current) {
      const filteredQuery = getAllowedRedirectSearchQuery(queryObj);
      updateURL?.(
        stringify({
          ...filteredQuery,
          listType,
          ...(isMobile ? { activeTabId: TAB_ID.ROOMS_AND_PLANS } : {}),
        }),
      );
    }
  };

  const onSubmitFilter = useCallback(() => {
    if (formEl.current) {
      const formData = new FormData(formEl.current);
      const search = new URLSearchParams(formData as any);
      search.set('listType', listType);
      isMobile && search.set('activeTabId', TAB_ID.ROOMS_AND_PLANS);
      const queryString = search.toString();
      updateURL?.(queryString);
    }
  }, [isMobile, listType, updateURL]);

  // execute when selected tag removed
  const onToggleFilterTag = useCallback(
    (toggleOptions: PlanFilter) => {
      toggleFilterOptions({
        features: EMPTY_ARRAY,
        maxPrice,
        minPrice,
        freeCancellation,
        ...toggleOptions,
      });
    },
    [maxPrice, minPrice, freeCancellation, toggleFilterOptions],
  );

  const onRemovePriceRange = useCallback(() => {
    onToggleFilterTag({ maxPrice: null, minPrice: null });
    setTimeout(() => {
      onSubmitFilter();
    }, 0);
  }, [onToggleFilterTag, onSubmitFilter]);

  const onToggleFeatures = useCallback(
    (ids: Array<string>) => {
      onToggleFilterTag({ features: ids });
      setTimeout(() => {
        onSubmitFilter();
      }, 0);
    },
    [onToggleFilterTag, onSubmitFilter],
  );

  const quickFilterItems = quickFilters.map(filterItem => (
    <FilterTag
      key={filterItem.featureId}
      value={filterItem.name}
      customClass={styles.quickFilterItem}
      isChecked={filterItem.selected}
      selectedTagClass={styles.selectedQuickFilterItem}
      onClick={() => onToggleFeatures([filterItem.featureId])}
      testId={QUICK_FILTER_TAG_TESTID}
    />
  ));

  const onRemoveFreeCancellation = useCallback(() => {
    onToggleFilterTag?.({ freeCancellation: false });
    setTimeout(() => onSubmitFilter?.(), 0);
  }, [onSubmitFilter, onToggleFilterTag]);

  const onChangeSortKey = (filterGroupId: string, toggleOption: string) => {
    setSortKey(toggleOption as SortKeysType);
  };

  const filterButton = isNotEmptyArray(filterOptions) && (
    <FilterButton
      onPage={onPage}
      listType={listType}
      onClickSearch={onSubmitFilter}
      total={total}
      features={features}
      maxPrice={maxPrice}
      minPrice={minPrice}
      onClear={onClearFilter}
      onReset={onResetFilter}
      onFilterChange={onFilterChange}
      onPriceSliderUpdate={onPriceSliderUpdate}
      filterCount={filterCount}
      filterContentWrapper={styles.filterContentWrapper}
      setFilterButtonWidth={setFilterButtonWidth}
      onChangeSortKey={onChangeSortKey}
      sortKey={sortKey}
      freeCancellation={freeCancellation}
      onFreeCancellationUpdate={onFreeCancellationUpdate}
    />
  );

  // This function toggles visibility (priceSort popup) based on horizontal scrolling within the quick filter container.
  const handleOnScroll = (element: HTMLElement) => {
    element.scrollLeft > 0 ? setHidePricePopup(true) : setHidePricePopup(false);
  };

  return (
    <>
      <Form
        ref={formEl}
        className={cx(props.className, styles.wrapper)}
        onSubmit={onSubmitCondition}
        data-testid="searchForm-wrapper"
      >
        {isNotEmptyArray(features) && (
          <input type="hidden" name="features" value={features?.join(',')} />
        )}
        {maxPrice && <input type="hidden" name="maxPrice" value={String(maxPrice)} />}
        {minPrice && <input type="hidden" name="minPrice" value={String(minPrice)} />}
        {freeCancellation && (
          <input type="hidden" name="freeCancellation" value={String(freeCancellation)} />
        )}
        {/* SEO Params */}
        {sortKey && <input type="hidden" name={seoKeys.sortKey} value={sortKey} />}
        {/* search input */}
        <div className={styles.searchBar}>
          {isPC && (
            <h2 data-testid="providerInfo-searchFrom-header">
              <Translate
                id="Provider_Info.Explore_Our_Rooms_And_Plans"
                className={styles.searchBarText}
              />
            </h2>
          )}
          {/* Translation off the translation for the date input section: https://jira.rakuten-it.com/jira/browse/TIDP-28464 */}
          <div className={styles.inputContainer} translate="no">
            {isMobile && filterButton}
            <DateInput
              className={styles.dateInput}
              startDate={query.startDate}
              endDate={query.endDate}
              onPage={onPage}
              onClickOkButton={isMobile ? triggerFormSubmit : undefined}
              onDateChanged={onDatesSet}
              isAutoOpenDatePicker={isAutoOpenDatePicker}
              setIsAutoOpenDatePicker={setIsAutoOpenDatePicker}
            />
            <AmountInput
              onClickOkButton={isMobile ? triggerFormSubmit : undefined}
              className={styles.amountInput}
              onPage={onPage}
              defaultAdults={Number(query.adults)}
              defaultRooms={Number(query.noOfUnits)}
              defaultChildrenAges={
                query.childrenAges ? query.childrenAges.split(',').map(Number) : EMPTY_ARRAY
              }
              isDatesSet={isDatesSet}
              onCloseClick={triggerFormSubmit}
            />
            <SalesPromotionInput
              salesPromotionId={props.query.salesPromotionId}
              promotionCode={props.query.promotionCode}
              isCouponSpecified={props.query.isCouponSpecified}
            />
            {!isMobile && (
              <FlatButton
                classType="secondary"
                type="submit"
                className={styles.searchButton}
                isLoading={props.isFetching}
                data-testid="providerInfo-searchFrom-submitButton"
              >
                <Translate id="Provider_Info.Update" />
              </FlatButton>
            )}
          </div>
        </div>

        {/* Room filters and Page Listing view Selector Tabs */}
        <div className={styles.tagFilterAndTabListWrapper} ref={props.tagFilterAndTabListRef}>
          {!isMobile && filterButton}
          {!isMobile && (
            <div
              className={styles.quickFilterWrapper}
              ref={quickFilterWrapperRef}
              style={{ width: `calc(100% - ${quickFiltersThresholdWidth}px` }} // Calculate the width to display quick filters between the filter button and tab selector
            >
              <HorizontalNavScroll onScroll={handleOnScroll}>
                {initialOptions.length > 0 && (
                  <SortSelectBox
                    className={styles.sortBox}
                    query={query}
                    params={params}
                    onPage={PROVIDER_INFO}
                    popupWrapperClass={
                      isPricePopupOpen && hidePricePopup
                        ? cx(styles.sortBoxPopup, styles.hideSortBoxPopup)
                        : styles.sortBoxPopup
                    }
                    onAppearanceChanged={isOpen => setPricePopupOpen(isOpen)}
                  />
                )}
                {!isTab && quickFilters.length > 0 && quickFilterItems}
              </HorizontalNavScroll>
            </div>
          )}

          {/* removes tab when there are no rooms to be shown. */}
          {/* checking just rooms since plans can not exist without rooms */}
          {isNotEmptyArray(items) && listingTypeSelectorTabList}
        </div>
        {!isMobile && (
          <div className={cx(styles.selectedFiltersWrapper, isScrollingDown && styles.hide)}>
            <ProviderFilterTags
              appliedMin={searchedMaxPrice}
              appliedMax={searchedMinPrice}
              isGroupedFeatures
              filters={initialOptions}
              coupon={currentCoupon}
              onRemovePriceRange={onRemovePriceRange}
              onRemoveFeatures={onToggleFeatures}
              onRemoveCoupon={onRemoveCoupon}
              appliedQuickFilters={quickFilters}
              onRemoveFreeCancellation={onRemoveFreeCancellation}
            />
          </div>
        )}
      </Form>
    </>
  );
}

SearchForm.defaultProps = {
  // make the page able to be accessed without query string
  query: EMPTY_OBJECT,
};

export default SearchForm;
